// crewbit-admin/src/components/money/MoneyDisputeDetailPanel.tsx
//
// Tier 2-UI Phase E (in-app bundle) — admin slide-over panel for single
// dispute. Mounts from /money/disputes via URL search-param (?dispute_id=…).
// disputeId === null keeps the SlidePanel closed; transition animates.
//
// Loads disputes envelope via useDisputeEscalations + filters by id. No
// inline mutations — admin v1 punts in-app evidence submission to Stripe
// Dashboard via deep-link (§E-inapp.D2). MONEY_MUTATION_EVENT listener on
// useBusinessFinancialSettings already covers refresh propagation; this
// panel re-fetches on open via useEffect.

import { useCallback, useEffect, useState } from 'react';
import { SlidePanel } from '../common/SlidePanel';
import { palette, spacing, typography } from '../../styles/tokens';
import { useActiveBusiness } from '../../hooks/useActiveBusiness';
import { useDisputeEscalations } from '../../hooks/useDisputeEscalations';
import DisputeDetailContent from './DisputeDetailContent';
import type { DisputeEscalation, DisputeStatus } from '../../types/disputes';

const STATUS_LABELS: Record<DisputeStatus, string> = {
  opened: 'Open',
  evidence_submitted: 'Under review',
  won: 'Won',
  lost: 'Lost',
};

const COPY = {
  stripeSays: (value: string) => `Stripe says: ${value}`,
  invoiceLabel: 'Invoice',
  invoiceMissing: 'Invoice unavailable',
  clientLabel: 'Client',
  clientUnknown: 'Unknown client',
  invoiceTotalLabel: 'Invoice total',
  reasonLabel: 'Reason',
  openedAtLabel: 'Opened',
  evidenceSubmittedLabel: 'Evidence submitted',
  resolvedAtLabel: 'Resolved',
  resolutionAmountLabel: 'Amount lost',
  openInStripe: 'Open in Stripe Dashboard',
  openInStripeHelper: 'Submit evidence in Stripe. Status updates here automatically once Stripe webhook fires.',
};

interface Props {
  disputeId: string | null;
  onClose: () => void;
}

export function MoneyDisputeDetailPanel({ disputeId, onClose }: Props) {
  const isOpen = disputeId !== null;
  const business = useActiveBusiness();
  const businessId = business.activeBusinessId ?? null;
  const fetchDisputes = useDisputeEscalations();

  const [dispute, setDispute] = useState<DisputeEscalation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!businessId || !disputeId) return;
    setIsLoading(true);
    try {
      const result = await fetchDisputes({ businessId });
      if (result.success) {
        setDispute(result.data.disputes.find((d) => d.id === disputeId) ?? null);
        setErrorCode(null);
      } else {
        setErrorCode(result.error.code);
      }
    } finally {
      setIsLoading(false);
    }
  }, [businessId, disputeId, fetchDisputes]);

  useEffect(() => {
    if (isOpen) void load();
    else {
      setDispute(null);
      setErrorCode(null);
    }
  }, [isOpen, load]);

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title="Dispute" width={520}>
      {isLoading && !dispute ? (
        <div style={{ padding: spacing.xl, textAlign: 'center', color: palette.g500 }}>
          Loading…
        </div>
      ) : errorCode && !dispute ? (
        <div style={{ padding: spacing.xl, color: palette.red }}>
          <div style={{ fontSize: typography.size.subheading, fontWeight: 600, marginBottom: spacing.xs }}>
            Couldn't load dispute
          </div>
          <div style={{ fontSize: typography.size.caption }}>{errorCode}</div>
        </div>
      ) : !dispute ? (
        <div style={{ padding: spacing.xl, color: palette.g600 }}>
          <div style={{ fontSize: typography.size.subheading, fontWeight: 600, marginBottom: spacing.xs }}>
            Dispute not found
          </div>
          <div style={{ fontSize: typography.size.caption }}>
            This dispute is no longer in your list.
          </div>
        </div>
      ) : (
        <div style={{ padding: spacing.lg }}>
          <DisputeDetailContent dispute={dispute} statusLabels={STATUS_LABELS} copy={COPY} />
        </div>
      )}
    </SlidePanel>
  );
}
